import { useCallback, useEffect, useRef, useState } from "react";
import signalsApi from "../../services/signalsApi";
import engineApi from "../../services/engineApi";
import { normalizeNonce, isSignable } from "./enrollContract";
import { classifyWalletError, inspectSiwe } from "./walletRegistry";

/**
 * useWalletRegistry — React state around the multi-wallet registry (ARC-46).
 *
 * THE ADD SEQUENCE, AND WHY IT STOPS IN THE MIDDLE:
 *
 *   1. the member picks the wallet in their own extension  (SELECTING)
 *   2. GET /enroll/nonce?wallet=…  → { nonce, message }    (NONCE)
 *   3. ── the member reads the domain and the message ──   (REVIEW)  ← pause
 *   4. that wallet personal_signs the message VERBATIM     (SIGNING)
 *   5. POST /wallets { wallet, signature, nonce, label? }  (SUBMITTING)
 *
 * Step 3 is the point. A signature prompt that appears on its own — before the
 * member has read WHO is asking — is exactly the pattern this app refuses
 * elsewhere, and the domain is the only part of a SIWE message that answers
 * that question. So the flow fetches the challenge, shows it, and waits.
 *
 * The signature is free and off-chain: it sends no transaction, moves nothing,
 * and grants no spending or trading permission. It proves one thing — that this
 * browser holds the key for that address. The member's wallet signs; this hook
 * never sees key material and this app never signs anything.
 *
 * The nonce path is the SAME one enrollment uses (enrollFlow.js). One proof
 * implementation for the whole app: a second dialect is a second thing to get
 * wrong, and the engine deliberately serves both routes off one nonce store.
 *
 * Errors are classified once, by walletRegistry.classifyWalletError, so a
 * declined signature, a spent nonce and a wallet on someone else's account each
 * get their own sentence instead of sharing a generic failure.
 */

export const ADD_STEP = Object.freeze({
  IDLE: "idle",
  SELECTING: "selecting",
  NONCE: "nonce",
  REVIEW: "review",
  SIGNING: "signing",
  SUBMITTING: "submitting",
  DONE: "done",
});

const noop = () => {};

export default function useWalletRegistry({
  enabled = false,
  signMessage,
  requestAccountSelection,
  onChanged = noop,
} = {}) {
  const supported = engineApi.walletRegistrySupported();

  const [wallets, setWallets] = useState(null); // null = not asked yet
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const [notEnrolled, setNotEnrolled] = useState(false);

  const [addStep, setAddStep] = useState(ADD_STEP.IDLE);
  const [challenge, setChallenge] = useState(null);
  const [addError, setAddError] = useState(null);
  const [added, setAdded] = useState(null); // the address of the last success

  const [busyAddress, setBusyAddress] = useState(null);
  const [actionError, setActionError] = useState(null);

  /* Unmounting mid-request must not set state on a dead component, and a
     refresh that lands after the panel closed is not worth an error banner. */
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    if (!enabled || !supported) return;
    setLoading(true);
    setListError(null);
    try {
      const list = await engineApi.getWallets();
      if (!alive.current) return;
      setWallets(list);
      setNotEnrolled(false);
    } catch (e) {
      if (!alive.current) return;
      /* 404 is not a failure: it is the answer for a member who has not
         enrolled their first wallet yet. An empty list plus the reason beats an
         error banner telling them something broke. */
      if (engineApi.isNotEnrolledError(e)) {
        setWallets([]);
        setNotEnrolled(true);
      } else {
        setListError(classifyWalletError(e, { action: "list" }));
      }
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [enabled, supported]);

  useEffect(() => {
    load();
  }, [load]);

  /* ── add, part one: pick the wallet and fetch its challenge ─────────────── */
  const beginAdd = useCallback(async () => {
    setAddError(null);
    setAdded(null);
    setChallenge(null);
    try {
      setAddStep(ADD_STEP.SELECTING);
      const address = await requestAccountSelection();

      setAddStep(ADD_STEP.NONCE);
      const c = normalizeNonce(await signalsApi.getEnrollNonce(address));
      if (!isSignable(c)) {
        throw new Error(
          "The engine returned an incomplete sign-in request. Nothing was signed — please try again."
        );
      }
      if (!alive.current) return false;

      /* Stop here. The panel renders the domain, the URI and the message the
         wallet is about to show, and the member decides. */
      setChallenge({ ...c, address, siwe: inspectSiwe(c.message) });
      setAddStep(ADD_STEP.REVIEW);
      return true;
    } catch (e) {
      if (!alive.current) return false;
      setAddError(classifyWalletError(e, { action: "add" }));
      setAddStep(ADD_STEP.IDLE);
      return false;
    }
  }, [requestAccountSelection]);

  /* ── add, part two: sign it and send it ─────────────────────────────────── */
  const confirmAdd = useCallback(
    async ({ label } = {}) => {
      if (!challenge) return false;
      setAddError(null);
      try {
        setAddStep(ADD_STEP.SIGNING);
        const signature = await signMessage(challenge.message, challenge.address);

        setAddStep(ADD_STEP.SUBMITTING);
        const res = await engineApi.attachWallet({
          wallet: challenge.address,
          signature,
          nonce: challenge.nonce,
          label: label && label.trim() ? label.trim() : undefined,
        });
        if (!alive.current) return true;

        setWallets(res.wallets);
        setNotEnrolled(false);
        setAdded(challenge.address);
        setAddStep(ADD_STEP.DONE);
        setChallenge(null);
        /* The header's slot total is /status.staking, not /wallets — a wallet
           holding staked Supernovas changes it, so the tab refetches. */
        onChanged();
        return true;
      } catch (e) {
        if (!alive.current) return false;
        setAddError(classifyWalletError(e, { action: "add" }));
        /* Back to IDLE, not REVIEW: the nonce was consumed by the attempt
           (single-use, spent before verification), so the challenge on screen
           is dead and re-signing it would fail a second time. */
        setAddStep(ADD_STEP.IDLE);
        setChallenge(null);
        return false;
      }
    },
    [challenge, signMessage, onChanged]
  );

  const cancelAdd = useCallback(() => {
    setAddStep(ADD_STEP.IDLE);
    setChallenge(null);
    setAddError(null);
    setAdded(null);
  }, []);

  /* ── rename: cosmetic, engine-persisted so it follows the member ────────── */
  const rename = useCallback(
    async (address, label) => {
      setActionError(null);
      setBusyAddress(address);
      try {
        const list = await engineApi.renameWallet(address, label && label.trim() ? label.trim() : null);
        if (alive.current) setWallets(list);
        return true;
      } catch (e) {
        if (alive.current) setActionError(classifyWalletError(e, { action: "label" }));
        return false;
      } finally {
        if (alive.current) setBusyAddress(null);
      }
    },
    []
  );

  /* ── remove ─────────────────────────────────────────────────────────────
     A 409 here is not a failure to report as one: it is the engine telling the
     member what to do first (deallocate these pairs), and that sentence reaches
     the screen intact rather than becoming "removal failed". */
  const remove = useCallback(
    async (address) => {
      setActionError(null);
      setBusyAddress(address);
      try {
        const list = await engineApi.detachWallet(address);
        if (alive.current) setWallets(list);
        onChanged();
        return true;
      } catch (e) {
        if (alive.current) setActionError(classifyWalletError(e, { action: "remove" }));
        return false;
      } finally {
        if (alive.current) setBusyAddress(null);
      }
    },
    [onChanged]
  );

  return {
    supported,
    wallets,
    loading,
    listError,
    notEnrolled,
    refresh: load,

    addStep,
    addBusy:
      addStep === ADD_STEP.SELECTING ||
      addStep === ADD_STEP.NONCE ||
      addStep === ADD_STEP.SIGNING ||
      addStep === ADD_STEP.SUBMITTING,
    challenge,
    addError,
    added,
    beginAdd,
    confirmAdd,
    cancelAdd,

    busyAddress,
    actionError,
    clearActionError: () => setActionError(null),
    rename,
    remove,
  };
}
